import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { TFMService } from 'app/mock-api/tfm.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user',
    standalone     : true,
    imports        : [MatButtonModule, MatMenuModule, NgIf, MatIconModule, NgClass, MatDividerModule],
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    @Input() showSummary: boolean = false;
    @Input() summaryAlign: 'left' | 'right' = 'right';
    user: User;
    rightsLabel: string | null = null;
    identityLabel: string | null = null;
    displayName: string | null = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _tfmService: TFMService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                this.user = user;
                this.rightsLabel = this.resolveRightsLabel(user);
                this.identityLabel = this.resolveIdentityLabel(user);
                this.displayName = this.resolveDisplayName(user);

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this._tfmService.getLoggedInUser()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((tfmUser: any) =>
            {
                this.mergeLoggedInUser(tfmUser);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.user )
        {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
            status,
        }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }

    private resolveRightsLabel(user: any): string | null
    {
        const override = this.getAdminViewOverride();
        if (override === 'admin') {
            return 'ADMIN (override)';
        }
        if (override === 'user') {
            return 'AGENT (override)';
        }

        const raw =
            user?.agentType ??
            user?.agent?.agentType ??
            user?.agent?.AgentType ??
            user?.role ??
            user?.rights ??
            user?.user_rights;

        if (Array.isArray(raw)) {
            const labels = raw
                .map((value) => String(value).trim())
                .filter((value) => value.length > 0);
            return labels.length ? labels.join(', ') : null;
        }

        if (raw !== undefined && raw !== null) {
            return String(raw).toUpperCase();
        }

        if (user?.isAdmin || user?.isadmin) {
            return 'ADMIN';
        }

        return null;
    }

    private resolveIdentityLabel(user: any): string | null
    {
        const candidates = [
            user?.emailAddress,
            user?.email,
            user?.userName,
            user?.username,
            user?.user_userName,
            user?.agent?.emailAddress,
            user?.agent?.email,
            user?.agent?.userName,
        ];

        for (const value of candidates) {
            if (typeof value === 'string' && value.trim().length > 0) {
                return value.trim();
            }
        }

        if (user?.id) {
            return String(user.id);
        }

        return null;
    }

    private resolveDisplayName(user: any): string | null
    {
        const first = user?.firstName ?? user?.first_name;
        const last = user?.lastName ?? user?.last_name;
        const combined = [first, last].filter(Boolean).join(' ').trim();
        if (combined) {
            return combined;
        }

        if (user?.name) {
            return String(user.name);
        }

        return this.resolveIdentityLabel(user);
    }

    private mergeLoggedInUser(tfmUser: any): void
    {
        if (!tfmUser) {
            return;
        }

        const firstName = tfmUser?.firstName ?? tfmUser?.first_name ?? tfmUser?.agent?.firstName ?? tfmUser?.agent?.first_name;
        const lastName = tfmUser?.lastName ?? tfmUser?.last_name ?? tfmUser?.agent?.lastName ?? tfmUser?.agent?.last_name;
        const combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();
        const email = tfmUser?.emailAddress
            ?? tfmUser?.email
            ?? tfmUser?.userName
            ?? tfmUser?.username
            ?? tfmUser?.user_userName
            ?? tfmUser?.agent?.emailAddress
            ?? tfmUser?.agent?.email
            ?? tfmUser?.agent?.userName;
        const userId = tfmUser?.id ?? tfmUser?.Id ?? tfmUser?.agent?.id ?? tfmUser?.agent?.Id;

        const merged: User = {
            ...(this.user || {}),
            ...tfmUser,
            id: userId !== undefined && userId !== null ? String(userId) : this.user?.id || '',
            firstName: firstName ?? this.user?.firstName,
            lastName: lastName ?? this.user?.lastName,
            name: combinedName || tfmUser?.name || this.user?.name || '',
            email: email || tfmUser?.email || this.user?.email || '',
            emailAddress: tfmUser?.emailAddress ?? email ?? this.user?.emailAddress,
            agentType: tfmUser?.agentType ?? tfmUser?.agent?.agentType ?? this.user?.agentType,
            isAdmin: tfmUser?.isAdmin ?? tfmUser?.isadmin ?? this.user?.isAdmin,
            isadmin: tfmUser?.isadmin ?? tfmUser?.isAdmin ?? this.user?.isadmin,
            avatar: tfmUser?.avatar ?? this.user?.avatar,
            status: tfmUser?.status ?? this.user?.status,
        };

        this.user = merged;
        this.rightsLabel = this.resolveRightsLabel(merged);
        this.identityLabel = this.resolveIdentityLabel(merged);
        this.displayName = this.resolveDisplayName(merged);
        this._userService.user = merged;

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    getAdminViewOverride(): 'admin' | 'user' | null
    {
        return this._tfmService.getAdminViewOverride();
    }

    isAdminViewMode(mode: 'admin' | 'user'): boolean
    {
        return this.getAdminViewOverride() === mode;
    }

    setAdminViewOverride(mode: 'admin' | 'user'): void
    {
        this._tfmService.setAdminViewOverride(mode);
        if (typeof window !== 'undefined')
        {
            window.location.reload();
        }
    }

    clearAdminViewOverride(): void
    {
        this._tfmService.setAdminViewOverride(null);
        if (typeof window !== 'undefined')
        {
            window.location.reload();
        }
    }
}
